import { Injectable } from '@angular/core';

export interface PlanFeatures {
  listingDuration: number;
  imagesAllowed: number;
  descriptionLength: number;
  appearsHigherInSearch: boolean;
  listingInFeaturedSection: boolean;
  listingInSuggestedItems: boolean;
  freeRelist: boolean;
}

export interface PlanDisplayFeatures {
  imagesAllowed: string;
  descriptionLength: string;
  appearsHigherInSearch: string;
  listingInFeaturedSection: string;
  listingInSuggestedItems: string;
  freeRelist: string;
}

export interface Plan {
  name: string;
  price: number;
  color: string;
  gradient: string;
  features: PlanFeatures;
  displayFeatures: PlanDisplayFeatures;
  popular: boolean;
  searchPriority: number;
}

export interface PlanValidationResult {
  valid: boolean;
  errors: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PlanConfigService {
  private plans: Plan[] = [
    {
      name: 'Free',
      price: 0,
      color: '#6c757d',
      gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
      features: {
        listingDuration: 7,
        imagesAllowed: 7,
        descriptionLength: 300,
        appearsHigherInSearch: false,
        listingInFeaturedSection: false,
        listingInSuggestedItems: false,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '7 Images',
        descriptionLength: '300 Characters',
        appearsHigherInSearch: 'No',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        freeRelist: 'No',
      },
      popular: false,
      searchPriority: 1,
    },
    {
      name: 'Starter',
      price: 1.99,
      color: '#3498db',
      gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
      features: {
        listingDuration: 14,
        imagesAllowed: 14,
        descriptionLength: 400,
        appearsHigherInSearch: true,
        listingInFeaturedSection: false,
        listingInSuggestedItems: false,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '14 Images',
        descriptionLength: '400 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        freeRelist: 'No',
      },
      popular: false,
      searchPriority: 2,
    },
    {
      name: 'Pro',
      price: 2.99,
      color: '#9b59b6',
      gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
      features: {
        listingDuration: 21,
        imagesAllowed: 21,
        descriptionLength: 500,
        appearsHigherInSearch: true,
        listingInFeaturedSection: true,
        listingInSuggestedItems: false,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '21 Images',
        descriptionLength: '500 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'No',
        freeRelist: 'No',
      },
      popular: true,
      searchPriority: 3,
    },
    {
      name: 'Elite',
      price: 3.99,
      color: '#e74c3c',
      gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
      features: {
        listingDuration: 42,
        imagesAllowed: 42,
        descriptionLength: 5000,
        appearsHigherInSearch: true,
        listingInFeaturedSection: true,
        listingInSuggestedItems: true,
        freeRelist: true,
      },
      displayFeatures: {
        imagesAllowed: '42 Images',
        descriptionLength: '5000 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'Yes',
        freeRelist: 'Yes (1 Free)',
      },
      popular: false,
      searchPriority: 4,
    },
  ];

  constructor() {}

  getAllPlans(): Plan[] {
    return [...this.plans];
  }

  getPlan(planName: string): Plan | null {
    return this.plans.find((plan) => plan.name === planName) || null;
  }

  getPlanFeatures(planName: string): PlanFeatures | null {
    const plan = this.getPlan(planName);
    return plan ? plan.features : null;
  }

  getPlanDisplayFeatures(planName: string): PlanDisplayFeatures | null {
    const plan = this.getPlan(planName);
    return plan ? plan.displayFeatures : null;
  }

  isValidPlan(planName: string): boolean {
    return this.plans.some((plan) => plan.name === planName);
  }

  getPlanPrice(planName: string): number {
    const plan = this.getPlan(planName);
    return plan ? plan.price : 0;
  }

  getPlanColor(planName: string): string {
    const plan = this.getPlan(planName);
    return plan ? plan.color : '#6c757d';
  }

  getPlanGradient(planName: string): string {
    const plan = this.getPlan(planName);
    return plan
      ? plan.gradient
      : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)';
  }

  getListingDuration(planName: string): number {
    const features = this.getPlanFeatures(planName);
    return features ? features.listingDuration : 7;
  }

  getMaxImages(planName: string): number {
    const features = this.getPlanFeatures(planName);
    return features ? features.imagesAllowed : 7;
  }

  getMaxDescriptionLength(planName: string): number {
    const features = this.getPlanFeatures(planName);
    return features ? features.descriptionLength : 300;
  }

  appearsHigherInSearch(planName: string): boolean {
    const features = this.getPlanFeatures(planName);
    return features ? features.appearsHigherInSearch : false;
  }

  isInFeaturedSection(planName: string): boolean {
    const features = this.getPlanFeatures(planName);
    return features ? features.listingInFeaturedSection : false;
  }

  isInSuggestedItems(planName: string): boolean {
    const features = this.getPlanFeatures(planName);
    return features ? features.listingInSuggestedItems : false;
  }

  hasFreeRelist(planName: string): boolean {
    const features = this.getPlanFeatures(planName);
    return features ? features.freeRelist : false;
  }

  getSearchPriority(planName: string): number {
    const plan = this.getPlan(planName);
    return plan ? plan.searchPriority : 1;
  }

  calculateExpirationDate(planName: string): Date {
    const duration = this.getListingDuration(planName);
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + duration);
    return expirationDate;
  }

  validateAgainstPlan(
    listingData: any,
    planName: string
  ): PlanValidationResult {
    const errors: string[] = [];
    const features = this.getPlanFeatures(planName);

    if (!features) {
      errors.push(`Invalid plan: ${planName}`);
      return { valid: false, errors };
    }

    if (listingData.images && Array.isArray(listingData.images)) {
      if (listingData.images.length > features.imagesAllowed) {
        errors.push(
          `Maximum ${features.imagesAllowed} images allowed for ${planName} plan`
        );
      }
    }

    if (
      listingData.description &&
      typeof listingData.description === 'string'
    ) {
      if (listingData.description.length > features.descriptionLength) {
        errors.push(
          `Description cannot exceed ${features.descriptionLength} characters for ${planName} plan`
        );
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  getRecommendedPlan(requirements: {
    images?: number;
    descriptionLength?: number;
    needsFeatured?: boolean;
  }): Plan {
    const {
      images = 0,
      descriptionLength = 0,
      needsFeatured = false,
    } = requirements;

    for (let i = this.plans.length - 1; i >= 0; i--) {
      const plan = this.plans[i];
      const features = plan.features;

      const meetsImageRequirement = images <= features.imagesAllowed;
      const meetsDescriptionRequirement =
        descriptionLength <= features.descriptionLength;
      const meetsFeaturedRequirement =
        !needsFeatured || features.listingInFeaturedSection;

      if (
        meetsImageRequirement &&
        meetsDescriptionRequirement &&
        meetsFeaturedRequirement
      ) {
        return plan;
      }
    }

    return this.plans[this.plans.length - 1];
  }

  getPlanUpgradeSuggestions(
    currentPlan: string,
    requirements: {
      images?: number;
      descriptionLength?: number;
    }
  ): { needsUpgrade: boolean; suggestedPlan?: Plan; reasons: string[] } {
    const current = this.getPlan(currentPlan);
    if (!current) {
      return { needsUpgrade: true, reasons: ['Invalid current plan'] };
    }

    const reasons: string[] = [];
    const { images = 0, descriptionLength = 0 } = requirements;

    if (images > current.features.imagesAllowed) {
      reasons.push(
        `Need ${images} images, current plan allows ${current.features.imagesAllowed}`
      );
    }

    if (descriptionLength > current.features.descriptionLength) {
      reasons.push(
        `Need ${descriptionLength} characters, current plan allows ${current.features.descriptionLength}`
      );
    }

    if (reasons.length === 0) {
      return { needsUpgrade: false, reasons: [] };
    }

    const suggestedPlan = this.getRecommendedPlan({
      images,
      descriptionLength,
    });

    return {
      needsUpgrade: true,
      suggestedPlan,
      reasons,
    };
  }

  getPlanComparison(): {
    feature: string;
    Free: string;
    Starter: string;
    Pro: string;
    Elite: string;
  }[] {
    return [
      {
        feature: 'Listing Duration',
        Free: '7 days',
        Starter: '14 days',
        Pro: '21 days',
        Elite: '42 days',
      },
      {
        feature: 'Images Allowed',
        Free: '7 images',
        Starter: '14 images',
        Pro: '21 images',
        Elite: '42 images',
      },
      {
        feature: 'Description Length',
        Free: '300 characters',
        Starter: '400 characters',
        Pro: '500 characters',
        Elite: '5000 characters',
      },
      {
        feature: 'Higher in Search',
        Free: '✗',
        Starter: '✓',
        Pro: '✓',
        Elite: '✓',
      },
      {
        feature: 'Email Notifications',
        Free: '✗',
        Starter: '✗',
        Pro: '✓',
        Elite: '✓',
      },
      {
        feature: 'Featured Section',
        Free: '✗',
        Starter: '✗',
        Pro: '✓',
        Elite: '✓',
      },
      {
        feature: 'Suggested Items',
        Free: '✗',
        Starter: '✗',
        Pro: '✗',
        Elite: '✓',
      },
      {
        feature: 'Free Relist',
        Free: '✗',
        Starter: '✗',
        Pro: '✗',
        Elite: '✓ (1 time)',
      },
    ];
  }

  formatPrice(planName: string): string {
    const price = this.getPlanPrice(planName);
    return price === 0 ? 'Free' : `$${price.toFixed(2)}`;
  }

  getPlanTier(planName: string): number {
    const planTiers: { [key: string]: number } = {
      Free: 0,
      Starter: 1,
      Pro: 2,
      Elite: 3,
    };
    return planTiers[planName] || 0;
  }

  isPlanHigher(planA: string, planB: string): boolean {
    return this.getPlanTier(planA) > this.getPlanTier(planB);
  }

  getNextTierPlan(currentPlan: string): Plan | null {
    const currentTier = this.getPlanTier(currentPlan);
    return (
      this.plans.find(
        (plan) => this.getPlanTier(plan.name) === currentTier + 1
      ) || null
    );
  }

  getPopularPlan(): Plan | null {
    return this.plans.find((plan) => plan.popular) || null;
  }
}
